import * as React from 'react'

import { useMutation } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AssignOwnerDocument,
  ItAssetDetailsQuery,
  ItAssetsTableQuery,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { type Location, LocationSelector } from 'modules/it_management/components/LocationSelector'

type Asset =
  | ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]
  | ItAssetDetailsQuery['itManagement']['itAsset']

interface Props {
  asset: Asset
  onClose: () => void
}

export const AssignOwnerModal = ({ asset, onClose }: Props) => {
  const [assignOwner, { loading: assigning }] = useMutation(AssignOwnerDocument)

  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | undefined>(
    asset?.owner?.id
  )
  const [selectedLocation, setSelectedLocation] = React.useState<Location | null>(
    asset?.location
      ? {
          id: String(asset.location.id),
          name: asset.location.name || '',
        }
      : null
  )

  const handleAssignOwnerAsset = async () => {
    if (!asset) return

    track(trackingEvents.itManagement.assigmentEquipment.submit)

    try {
      await assignOwner({
        variables: {
          id: asset.id,
          locationId: selectedLocation ? Number(selectedLocation.id) : null,
          ownerId: selectedEmployeeId ?? null,
        },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })
      track(trackingEvents.itManagement.assigmentEquipment.success)
      app.setFlash({
        type: 'correct',
        message:
          selectedEmployeeId && selectedLocation
            ? i18n.t('it_management.assets.assign_owner_modal.success_message')
            : i18n.t('it_management.assets.assign_owner_modal.unassigned_message'),
      })
      onClose()
    } catch (error) {
      track(trackingEvents.itManagement.assigmentEquipment.error)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.assign_owner_modal.error_message'),
      })
    }
  }

  const assetName = asset
    ? asset.itAssetModel?.brand && asset.itAssetModel?.name
      ? `${asset.itAssetModel.brand} ${asset.itAssetModel.name}`
      : asset.serialNumber || i18n.t('it_management.assets.assign_owner_modal.unknown_asset')
    : i18n.t('it_management.assets.assign_owner_modal.unknown_asset')

  return (
    <Modal
      title={i18n.t('it_management.assets.assign_owner_modal.title')}
      onClose={onClose}
      style={{
        top: '-5%',
      }}
      illustration={'user'}
      description={i18n.t('it_management.assets.assign_owner_modal.description', { assetName })}
      disableFocusTrap
      shouldCloseOnOverlayClick={false}
      onSubmit={handleAssignOwnerAsset}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={assigning}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="primary" submit disabled={assigning}>
            {assigning
              ? i18n.t('it_management.assets.assign_owner_modal.assigning')
              : selectedEmployeeId || selectedLocation
              ? i18n.t('it_management.assets.assign_owner_modal.assign')
              : i18n.t('it_management.assets.assign_owner_modal.clear_assignment')}
          </Button>
        </Box>
      }
    >
      <Box gap="s32" maxWidth="s400">
        <Box gap="s16">
          <Box gap="s8">
            <Text.Normal size="200" weight="medium" color="grey800">
              {i18n.t('it_management.assignment_step.employee')}
            </Text.Normal>
            <EmployeeSelectorV2
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              forcedFilters={{ includeInactive: true }}
            />
          </Box>

          <LocationSelector
            value={selectedLocation}
            onSelect={setSelectedLocation}
            label={i18n.t('it_management.assignment_step.space')}
            emptyLabel={i18n.t('it_management.assignment_step.empty_label')}
          />

          <Box gap="s8">
            <Text.Normal size="200" color="grey600">
              {selectedEmployeeId && selectedLocation
                ? i18n.t('it_management.assignment_step.assignment_help')
                : i18n.t('it_management.assignment_step.clear_help')}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
